import { Text, Box, Flex, Loader } from '@mantine/core';
import { useMemo } from 'react';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useSelectableUploadItems } from '../hooks/useSelectableUploadItems';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SuitabilitySelectableList } from '../edit/components/SuitabilitySelectableList';
import { useAllowedFileTypes } from 'src/data-capture/file-management/allowedFileTypes';
import { UploadPillButton } from '../edit/components/UploadPillButton';
import { StepProps } from '../types';
import { SelectedCountPill } from '../edit/components/SelectedCountPill';
import { getClientId } from '../edit/utils/getClientId';
import { useFilteredItems } from '../hooks/useFilteredItems';
import { useItemSelectionMapping } from '../hooks/useItemSelectionMapping';
import { useCombinedFileUpload } from '../hooks/useCombinedFileUpload';

export function FeeDocumentsStep({ data, setData, filter, searchBar }: StepProps) {
  const clientId = getClientId(data);
  const familyId = data.isFamily ? data.family?.id : undefined;
  const { files, isLoading } = useFileUploads({ clientId, familyId });
  const items = useSelectableUploadItems(files);

  const selectedIds = useMemo(() => data.feeDocuments.map(d => d.id), [data.feeDocuments]);
  const selectedCount = data.feeDocuments.length;

  const { extensions, fileButtonAccept } = useAllowedFileTypes();
  const { isUploading, handleFileUpload, handleOneDriveUpload } = useCombinedFileUpload({
    clientId,
    familyId,
    onUploaded: uploadedFiles => {
      setData(prev => ({
        ...prev,
        feeDocuments: [...prev.feeDocuments, ...uploadedFiles],
      }));
    },
    onLoadingChange: isLoading => {
      setData(prev => ({ ...prev, isLoading }));
    },
  });

  const filteredItems = useFilteredItems(items, filter);
  const mapIdsToSelectedFiles = useItemSelectionMapping(items);

  const title = data.isFamily
    ? `Include fee documentation for ${data.family?.name} family`
    : `Include fee documentation for ${data.client?.firstName} ${data.client?.lastName}`;

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard
        title={title}
        subTitle="Select any documents that contain fee information to be referenced in the report. This includes provider fee schedules, platform charges, fund costs, and adviser fee agreements."
        searchBar={searchBar}
      >
        <Flex align="center" justify="space-between" w="100%" mb="lg" px="xxxs" gap="xxs">
          <SelectedCountPill selectedCount={selectedCount} />
          <Text fw={FONT_WEIGHTS.medium} size="xs" c="gray.4" flex={1} miw={0} mx={16}>
            Only include documents that contain fee-related information. The fees extracted from
            these documents will be used to populate fee disclosures in the suitability report.
          </Text>
          <UploadPillButton
            loading={isUploading}
            onFilesSelected={handleFileUpload}
            onOneDriveFilesSelected={handleOneDriveUpload}
            accept={fileButtonAccept}
            fileExtensions={extensions}
            label="Upload"
          />
        </Flex>
        <Box flex={1} mih={0} style={{ overflow: 'auto' }}>
          {isLoading ? (
            <Flex justify="center" align="center" h="100%">
              <Loader data-testid="loader" />
            </Flex>
          ) : (
            <SuitabilitySelectableList
              items={filteredItems.map(f => ({
                id: f.id,
                title: f.title,
                description: f.description,
                file: f.file,
              }))}
              selectMultiple
              selectedItemIds={selectedIds}
              setSelectedItemIds={ids =>
                setData(prev => ({
                  ...prev,
                  feeDocuments: mapIdsToSelectedFiles(ids),
                }))
              }
              highlightedElement={data.highlightedElement}
              emptyView={<Text c="gray.6">No fee documents found</Text>}
            />
          )}
        </Box>
      </StepCard>
    </Flex>
  );
}
